import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CloseIcon from "../../../assets/icons/close.svg";
import Select, { components } from "react-select";
import APIVariables from "../../../services/api-variables";
import { ReactComponent as DropdownArrowDown } from "../../../assets/icons/dropdownArrowDown.svg";
import { ReactComponent as DropdownArrowUp } from "../../../assets/icons/dropdownArrowUp.svg";
import { ReactComponent as Undo } from "../../../assets/icons/undo.svg";
import { handleCatch } from "../../../shared/helpers";
import { App } from "../../../routes/Constants";
import QuitModal from "../../common/Modal/QuitModal";
import ConfirmHomecareModal from "./modals/ConfirmHomecareModal";
import Axios from "../../../services/Axios";

const inputStyles =
  "mt-2.5 mb-[15px] w-full border-0 border-b border-[#c8c8c8] bg-[#f8f8f8] px-0 py-1 text-lg leading-[22px] text-[#646464] outline-none disabled:cursor-not-allowed";

const DropdownIndicator = (props) => {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        {props.selectProps.menuIsOpen ? (
          <DropdownArrowUp />
        ) : (
          <DropdownArrowDown />
        )}
      </components.DropdownIndicator>
    )
  );
};

const HomecareAdd = () => {
  const navigate = useNavigate();
  const [homecares, setHomecares] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quitModalIsOpen, setIsOpenQuit] = useState(false);
  const [confirmModalIsOpen, setConfirmModal] = useState(false);
  const [validateScreenFields, setValidateScreenFields] = useState({});
  const [form, setForm] = useState({});

  useEffect(() => {
    setValidateScreenFields(validateForm());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  useEffect(() => {
    getHomecaresList();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getHomecaresList = async () => {
    await Axios.get(APIVariables.HCP_HOMECARES_LIST)
      .then((res) => {
        const { status, data } = res;
        if (status === 200) {
          setHomecares(data);
        }
      })
      .catch((error) => {
        handleCatch(error);
      });
  };

  const handleChange = (e) => {
    if (!e.target) {
      setForm((prevState) => ({
        ...prevState,
        homecare: e,
      }));
    } else {
      const { name, value } = e.target;

      setForm((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  function closeForm() {
    setIsOpenQuit(true);
  }

  function closeModalQuit() {
    setIsOpenQuit(false);
  }

  function quitModal() {
    navigate(App.HOMECARE);
  }

  const submitForm = async () => {
    setIsSubmitting(false);
    const areAllFields = Object.values(validateScreenFields).every((field) => {
      return field.isValid;
    });

    const payload = {
      connected_user_id: form.homecare?.connected_user_id,
    };

    if (areAllFields) {
      await Axios.post(APIVariables.USERS_SIGNUP_HCP_HOMECARE, payload)
        .then((res) => {
          const { status } = res;
          if (status === 200) {
            setConfirmModal(true);
          }
        })
        .catch(function (error) {
          handleCatch(error);
        });
    } else {
      setIsSubmitting(true);
    }
  };

  const validateForm = () => {
    const { homecare } = form;
    return {
      homecare: {
        isValid: homecare && Object.keys(homecare).length > 0,
      },
    };
  };

  const homecareMapOptions = () => {
    return (homecares || []).map((homecare) => ({
      label: (
        <div className="flex-justify custom-hover-select">
          <div>{homecare?.name}</div>
          <div>{homecare?.postcode}</div>
        </div>
      ),
      value: homecare.name + " " + homecare.postcode,
      ...homecare,
    }));
  };

  return (
    <div
      className="min-h-screen overflow-y-auto  pt-[140px] pb-[100px]"
      data-testid="homecare-add-page"
    >
      <div className="relative mx-auto max-w-[1100px] rounded-[10px] bg-white px-6 py-5 shadow-[0px_0px_40px_rgba(0,0,0,0.2)] sm:px-10">
        <button
          type="button"
          className="absolute right-[15px] top-[15px] cursor-pointer rounded-full bg-[#9ABDB8] p-[3px]"
          onClick={closeForm}
          aria-label="Close form"
          data-testid="homecare-add-close-button"
        >
          <img src={CloseIcon} alt="" className="brightness-0 invert" />
        </button>
        <div className="font-size-18 blue-label">Step 1/1</div>
        <h1 className="select-category-txt mineShaft-color mt-2.5 pb-1.5">
          Homecare Connection Form
        </h1>
        <hr className="my-0 h-px border-0 bg-[#768692]" />

        <h2 className="title-details mineShaft-color mt-[30px] mb-[25px]">
          Homecare Details
        </h2>

        <div className="mb-5">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="relative mb-1 custom-select-placeholder">
              <label className="blue-label" htmlFor="homecare-select">
                Homecare Name*:
              </label>
              <Select
                inputId="homecare-select"
                classNamePrefix="custom-select"
                components={{
                  DropdownIndicator: DropdownIndicator,
                  IndicatorSeparator: () => null,
                }}
                name="homecare"
                onChange={handleChange}
                options={homecareMapOptions()}
                value={form.homecare}
                placeholder="Search Pharmacy name or Postcode"
                className={
                  isSubmitting && !validateScreenFields.homecare?.isValid
                    ? "error-select"
                    : "select-border"
                }
                data-testid="homecare-add-select"
              />
              {isSubmitting && !validateScreenFields.homecare?.isValid && (
                <span className="required-text">Required</span>
              )}
            </div>
            <div>
              <label className="blue-label" htmlFor="homecare-postcode">
                Postcode:
              </label>
              <input
                id="homecare-postcode"
                type="text"
                name="postcode"
                value={form.homecare?.postcode || ""}
                disabled
                className={inputStyles}
                data-testid="homecare-add-postcode-input"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="blue-label" htmlFor="homecare-address">
              Homecare Address:
            </label>
            <input
              id="homecare-address"
              type="text"
              name="homecare.address"
              value={form.homecare?.address || ""}
              disabled
              className={inputStyles}
              data-testid="homecare-add-address-input"
            />
          </div>
          <p className="info_nurse font-size-16 mt-9 leading-[15px] blue-label">
            <b className="font-size-16">IMPORTANT</b>:
            Connected Homecare providers will only receive an email of any PAF
            that has been sent
          </p>
        </div>

        <div className="flex flex-col gap-4 pt-[60px] sm:flex-row sm:items-center sm:justify-between">
          <p
            className="text-base blue-label"
            data-testid="homecare-add-mandatory-field-note"
          >
            *symbol indicating a mandatory field
          </p>
          <div className="flex items-center justify-end gap-3">
            {form?.homecare && Object.keys(form?.homecare).length ? (
              <button
                type="button"
                className="flex items-center gap-1 border-0 bg-transparent font-size-18 text-[#00567a] hover:text-[#8c8c8c]"
                onClick={() => setForm({ homecare: "" })}
                data-testid="homecare-add-undo-button"
              >
                <Undo /> Undo Changes
              </button>
            ) : null}

            <button
              type="button"
              className="primary-button"
              onClick={submitForm}
              data-testid="homecare-add-save-button"
            >
              Save
            </button>
          </div>
        </div>
      </div>

      {quitModalIsOpen && (
        <QuitModal
          quitModalIsOpen={quitModalIsOpen}
          closeModalQuit={closeModalQuit}
          quitModal={quitModal}
          description="Are you sure you want to cancel filling out this form? All progress will be lost and you will need to start again."
        />
      )}
      {confirmModalIsOpen && (
        <ConfirmHomecareModal
          closeModal={setConfirmModal}
          modalIsOpen={confirmModalIsOpen}
        />
      )}
    </div>
  );
};
export default HomecareAdd;
